/**
 * Offerte nate fuori dal gestionale.
 *
 * Quelle di prima che il gestionale esistesse, e quelle che ogni tanto si
 * scrivono a mano in Word: finché restano fuori, il lavoro che ne nasce non ha
 * una commessa, non ha fatture programmate e non compare in nessun controllo.
 *
 * Qui si carica il documento, il gestionale ne legge i campi e si crea
 * l'offerta come tutte le altre — con l'originale allegato, che è la carta
 * andata davvero al cliente. Niente viene salvato finché non si conferma.
 */
import { useState } from 'react';
import { offerteApi, allegatiApi } from '../api';
import Modal from './Modal';
import CampoImporto from './CampoImporto';

const oggi = () => new Date().toISOString().slice(0, 10);
const vuoto = () => ({
  numero: '', titolo: '', cliente_id: '', importo: '', stato: 'inviata',
  data_emissione: oggi(), data_scadenza: '', modalita_esecuzione: 'corpo',
  note: '', creaCommessa: false, numero_commessa: '',
});

export default function ImportaOffertaEsterna({ clienti = [], onCreata }) {
  const [aperto, setAperto] = useState(false);
  const [file, setFile] = useState(null);
  const [lettura, setLettura] = useState(null);     // esito dell'analisi
  const [form, setForm] = useState(vuoto());
  const [lavorando, setLavorando] = useState(false);
  const [errore, setErrore] = useState('');

  const apri = () => { setFile(null); setLettura(null); setForm(vuoto()); setErrore(''); setAperto(true); };
  const campo = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const leggi = async (f) => {
    setFile(f); setErrore(''); setLettura(null);
    if (!f) return;
    setLavorando(true);
    try {
      const r = await offerteApi.analizzaDocumento(f);
      setLettura(r);
      const c = r.campi || {};
      setForm(x => ({
        ...x,
        numero: c.numero || x.numero,
        titolo: c.descrizione || x.titolo || f.name.replace(/\.[^.]+$/, ''),
        importo: c.importo_max != null ? String(c.importo_max) : x.importo,
        data_emissione: c.data_offerta || x.data_emissione,
        data_scadenza: c.data_scadenza || x.data_scadenza,
        cliente_id: r.cliente?.id ? String(r.cliente.id) : x.cliente_id,
        modalita_esecuzione: c.modalita_esecuzione || x.modalita_esecuzione,
        note: c.note || x.note,
      }));
    } catch (e) {
      // Una scansione non si legge: l'offerta si compila a mano e il file si
      // allega lo stesso. Non è un errore che deve fermare il caricamento.
      setErrore(String(e?.error || e));
      setForm(x => ({ ...x, titolo: x.titolo || f.name.replace(/\.[^.]+$/, '') }));
    } finally { setLavorando(false); }
  };

  const salva = async () => {
    if (!form.numero.trim()) return setErrore('Il numero dell\'offerta è obbligatorio.');
    if (!form.titolo.trim()) return setErrore('L\'oggetto dell\'offerta è obbligatorio.');
    setLavorando(true); setErrore('');
    try {
      const creata = await offerteApi.create({
        numero: form.numero.trim(),
        titolo: form.titolo.trim(),
        cliente_id: form.cliente_id || null,
        importo: Number(String(form.importo).replace(',', '.')) || 0,
        stato: form.stato,
        data_emissione: form.data_emissione || null,
        data_scadenza: form.data_scadenza || null,
        modalita_esecuzione: form.modalita_esecuzione,
        note: [form.note, file ? `Offerta prodotta fuori dal gestionale, originale allegato (${file.name}).` : null]
          .filter(Boolean).join(' — ') || null,
      });
      // L'originale: è il documento che il cliente ha in mano.
      if (file) {
        try { await allegatiApi.carica('offerta', creata.id, file, 'preventivo'); }
        catch { setErrore('Offerta creata, ma l\'allegato non è stato caricato: riprova dalla scheda.'); }
      }
      if (form.creaCommessa && form.stato === 'vinta') {
        await offerteApi.converti(creata.id, {
          numero_commessa: form.numero_commessa.trim() || form.numero.trim(),
          data_inizio: form.data_emissione || null,
          modalita_esecuzione: form.modalita_esecuzione,
        });
      }
      setAperto(false);
      onCreata?.(creata);
    } catch (e) { setErrore(String(e?.error || e)); } finally { setLavorando(false); }
  };

  const avvertenze = lettura?.campi?.avvertenze || [];

  return (
    <>
      <button className="btn-secondary" onClick={apri}
              title="Registra un'offerta scritta fuori dal gestionale, allegando l'originale">
        ⬆ Carica offerta esterna
      </button>

      {aperto && (
        <Modal title="Carica un'offerta fatta fuori dal gestionale" onClose={() => setAperto(false)} size="lg">
          <div className="space-y-4 text-sm">
            <p className="text-xs text-gray-500">
              Serve per le offerte di prima del gestionale e per quelle preparate a mano: carica il
              documento, controlla i campi che ne vengono letti e salva. L'originale resta allegato
              all'offerta, e da lì in poi si gestisce come tutte le altre.
            </p>

            <div>
              <label className="label">Documento dell'offerta (PDF o Word)</label>
              <input type="file" accept=".pdf,.doc,.docx,application/pdf" className="input py-1.5"
                     onChange={e => leggi(e.target.files?.[0])} />
              {lavorando && !lettura && <p className="text-xs text-gray-500 mt-1">Leggo il documento…</p>}
              {lettura && (
                <p className="text-xs text-green-700 mt-1">
                  Letto {lettura.caratteri.toLocaleString('it-IT')} caratteri
                  {lettura.cliente && <> · cliente riconosciuto: <strong>{lettura.cliente.ragione_sociale || lettura.cliente.nome}</strong></>}
                </p>
              )}
              {/* Il cliente c'è nel documento ma non in anagrafica: va scelto
                  a mano, e va detto chi cercare. */}
              {lettura && !lettura.cliente && lettura.campi?.cliente_citato && (
                <p className="text-xs text-amber-700 mt-1">
                  Nel documento compare <strong>{lettura.campi.cliente_citato}</strong>, che non è in anagrafica
                  con questo nome: scegli il cliente qui sotto (o creane uno dalla pagina Clienti).
                </p>
              )}
            </div>

            {errore && (
              <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-800">{errore}</div>
            )}
            {avvertenze.length > 0 && (
              <div className="bg-gray-50 border rounded px-3 py-2 text-xs text-gray-600">
                {avvertenze.map((a, i) => <div key={i}>· {a}</div>)}
              </div>
            )}
            {lettura?.numero_gia_usato && (
              <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">
                Il numero <strong>{lettura.numero_gia_usato}</strong> è già usato da un'altra offerta:
                cambialo, oppure apri quella che c'è già.
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Numero offerta *</label>
                <input className="input font-mono" value={form.numero}
                       onChange={e => campo('numero', e.target.value)} />
              </div>
              <div>
                <label className="label">Cliente</label>
                <select className="input" value={form.cliente_id}
                        onChange={e => campo('cliente_id', e.target.value)}>
                  <option value="">— Scegli il cliente —</option>
                  {clienti.map(c => (
                    <option key={c.id} value={c.id}>{c.ragione_sociale || c.nome}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="label">Oggetto *</label>
                <input className="input" value={form.titolo} onChange={e => campo('titolo', e.target.value)} />
              </div>
              <div>
                <label className="label">Importo (€)</label>
                <CampoImporto className="input" value={form.importo} onChange={v => campo('importo', v)} />
              </div>
              <div>
                <label className="label">Stato</label>
                <select className="input" value={form.stato} onChange={e => campo('stato', e.target.value)}>
                  <option value="inviata">Inviata</option>
                  <option value="vinta">Vinta</option>
                  <option value="persa">Persa</option>
                  <option value="scaduta">Scaduta</option>
                  <option value="bozza">Bozza</option>
                </select>
              </div>
              <div>
                <label className="label">Data emissione</label>
                <input className="input" type="date" value={form.data_emissione || ''}
                       onChange={e => campo('data_emissione', e.target.value)} />
              </div>
              <div>
                <label className="label">Data scadenza</label>
                <input className="input" type="date" value={form.data_scadenza || ''}
                       onChange={e => campo('data_scadenza', e.target.value)} />
              </div>
              <div>
                <label className="label">Modalità</label>
                <select className="input" value={form.modalita_esecuzione}
                        onChange={e => campo('modalita_esecuzione', e.target.value)}>
                  <option value="corpo">A corpo (prezzo fisso)</option>
                  <option value="economia">A economia (a consuntivo)</option>
                </select>
              </div>
            </div>

            {/* Un'offerta già vinta serve a poco se non diventa una commessa:
                è lì che vivono fatture, rendiconti e controlli. */}
            {form.stato === 'vinta' && (
              <div className="bg-brand-50/70 border border-brand-100 rounded-lg p-3 space-y-2">
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="w-4 h-4 mt-0.5" checked={form.creaCommessa}
                         onChange={e => campo('creaCommessa', e.target.checked)} />
                  <span>
                    Crea subito la commessa
                    <span className="block text-xs text-gray-500">
                      Senza commessa l'offerta vinta resta ferma: niente fatture da emettere,
                      niente rendiconti, niente avanzamento.
                    </span>
                  </span>
                </label>
                {form.creaCommessa && (
                  <div className="w-64">
                    <label className="label text-xs">Numero della commessa</label>
                    <input className="input py-1 text-sm font-mono" placeholder={form.numero || 'come l\'offerta'}
                           value={form.numero_commessa} onChange={e => campo('numero_commessa', e.target.value)} />
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="label">Note</label>
              <input className="input" placeholder="es. offerta 2025 ripresa dalla cartella condivisa"
                     value={form.note} onChange={e => campo('note', e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setAperto(false)}>Annulla</button>
            <button className="btn-primary" disabled={lavorando} onClick={salva}>
              {lavorando ? 'Salvo…' : 'Crea offerta'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
